// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@eisinc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { colorsConfig } from "@eisinc/App/Widgets/DesignSystem/DummyData/DummyData";
import { clipboardText } from "@eisinc/App/Widgets/DesignSystem/Utilities";
import systemColors from "@eisinc/XDL/PreStyle/colors";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Toaster from "@insite/mobius/Toast/Toaster";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { get } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const ColorItem = ({
    color,
    text,
    colorDisplay,
    addBorder,
    styles,
}: {
    color: string;
    text: string;
    colorDisplay: string;
    styles: ColorItemStyles;
    addBorder: boolean;
}) => {
    const toaster = useToaster();

    const handleClick = () => {
        const isCopied = clipboardText(color);
        toaster.addToast({
            body: (
                <Typography>
                    {isCopied ? (
                        <>
                            <strong>{color}</strong> color is copied
                        </>
                    ) : (
                        <>
                            Copy <strong>{color}</strong> color failed
                        </>
                    )}
                </Typography>
            ),
            messageType: isCopied ? "success" : "danger",
        });
    };

    return (
        <Clickable
            onClick={handleClick}
            aria-label="Colors Tab"
            css={css`
                width: 100%;
                &:focus {
                    outline: none;
                }
            `}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper
                    css={css`
                        width: 100%;
                        height: 32px;
                        background: ${getColor(color)};
                        ${addBorder &&
                        css`
                            border: 1px solid ${getColor("common.ui2")};
                        `}
                    `}
                />
                <Typography {...styles.title}>{text}</Typography>
                <Typography {...styles.text}>{colorDisplay}</Typography>
                <Typography {...styles.subTitle}>{get(systemColors, color)}</Typography>
            </StyledWrapper>
        </Clickable>
    );
};

interface ColorItemStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
    text?: TypographyPresentationProps;
}

interface ColorsTabStyles {
    wrapperGridItem?: GridItemProps;
    pathTypography?: TypographyPresentationProps;
    colorItem: ColorItemStyles;
}

const colorsTabStyles: ColorsTabStyles = {
    wrapperGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pathTypography: {
        size: 15,
        css: css`
            margin: 15px 0;
            background: ${getColor("common.ui2")};
            color: ${getColor("secondary.main")};
            line-height: ${fontConfig.small.lineHeight};
            max-width: 100%;
            overflow: auto;
            padding: 15px 22px;
            display: block;
            word-wrap: break-word;
            user-select: none;
            border: 1px solid ${getColor("common.gray")};
            border-left: 3px solid ${getColor("warning.main")};
        `,
    },
    colorItem: {
        container: {
            css: css`
                overflow-wrap: break-word;
                width: 100%;
                display: flex;
                flex-direction: column;
                transition: transform 0.3s ease;
                &:hover {
                    transform: scale(1.1);
                }
                &:focus {
                    outline: none;
                }
            `,
        },
        title: {
            ...fontConfig.singleLineSmall,
            weight: fontConfig.weight.medium,
            css: css`
                display: block;
                margin-top: 15px;
                &:empty {
                    display: none;
                }
            `,
        },
        text: {
            ...fontConfig.singleLineSmall,
            weight: fontConfig.weight.medium,
            css: css`
                display: block;
                margin-top: 12px;
            `,
        },
        subTitle: {
            variant: "h5",
            weight: fontConfig.weight.regular,
            css: css`
                display: block;
                margin-top: 6px;
                margin-bottom: 0;
            `,
        },
    },
};

const styles = colorsTabStyles;
const ColorsTab: FC = prop => {
    return (
        <SectionContainer>
            <GridItem {...styles.wrapperGridItem}>
                <Toaster position="bottom" mobilePosition="bottom">
                    <Typography {...styles.pathTypography}>
                        {translate("Click on the color item to copy color path")}
                    </Typography>
                    <GridContainer
                        gap={20}
                        css={css`
                            > ${GridOffset} > ${GridItemStyle} {
                                padding-bottom: 30px;
                            }
                        `}
                    >
                        {Object.entries(colorsConfig).map(([category, items], index) => {
                            const isSmallSection = [0, 1, 4, 5].includes(index);
                            return (
                                <GridItem key={category} width={isSmallSection ? [12, 12, 12, 6, 6] : 12}>
                                    <GridContainer gap={8}>
                                        <GridItem width={12}>
                                            <SectionTitle>{category}</SectionTitle>
                                        </GridItem>
                                        <GridItem width={12}>
                                            <GridContainer gap={22}>
                                                {items.map((item, idx) => (
                                                    <GridItem
                                                        key={idx}
                                                        width={isSmallSection ? [6, 6, 4, 4, 4] : [6, 6, 4, 2, 2]}
                                                    >
                                                        <ColorItem
                                                            key={`color_${item.color}_${idx}`}
                                                            text={item.text ?? ""}
                                                            color={item.color ?? ""}
                                                            colorDisplay={item.colorDisplay ?? ""}
                                                            styles={styles.colorItem}
                                                            addBorder={"addBorder" in item ? !!item.addBorder : false}
                                                        />
                                                    </GridItem>
                                                ))}
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            );
                        })}
                    </GridContainer>
                </Toaster>
            </GridItem>
        </SectionContainer>
    );
};

export default ColorsTab;
